import React, { useState, useEffect } from 'react';
import styles from './HomeOwnerBilling.module.css';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Toast from '../../components/common/Toast/Toast';
import { PaymentIcon } from 'react-svg-credit-card-payment-icons';
import { StripeProvider } from '../../services/StripeContext';
import StripePaymentMethodModal from './billing/StripePaymentMethodModal';
import BillingAddressModal from './billing/BillingAddressModal';
import UpdateSubscriptionPaymentModal from './billing/UpdateSubscriptionPaymentModal';
import { homeownerBillingService } from '../../services/api';

const HomeOwnerBilling = () => {
  const navigate = useNavigate();
  const [currentPlan, setCurrentPlan] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [billingAddresses, setBillingAddresses] = useState([]);
  const [billingHistory, setBillingHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [isUpdatePaymentModalOpen, setIsUpdatePaymentModalOpen] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);
  const [editingAddress, setEditingAddress] = useState(null);
  const [toast, setToast] = useState(null);


  useEffect(() => {
    fetchBillingData();
    
    // Check for purchase completion flags and clear them
    const addonPurchaseCompleted = localStorage.getItem('addonPurchaseCompleted');
    const subscriptionCompleted = localStorage.getItem('subscriptionCompleted');
    
    if (addonPurchaseCompleted === 'true') {
      // console.log('Billing page: Detected addon purchase completion');
      localStorage.removeItem('addonPurchaseCompleted');
      localStorage.removeItem('addonPurchaseTimestamp');
      
      // Force a refresh after a shorter delay to ensure backend processing is complete
      setTimeout(() => {
        fetchBillingData();
        // Notify other components to refresh their data
        window.dispatchEvent(new CustomEvent('subscriptionUpdated'));
      }, 500); // Reduced from 1000ms to 500ms
    }
    
    if (subscriptionCompleted === 'true') {
      // console.log('Billing page: Detected subscription completion');
      localStorage.removeItem('subscriptionCompleted');
      localStorage.removeItem('subscriptionTimestamp');
      
      // Force a refresh after a shorter delay
      setTimeout(() => {
        fetchBillingData();
        // Notify other components to refresh their data
        window.dispatchEvent(new CustomEvent('subscriptionUpdated'));
      }, 500); // Reduced from 1000ms to 500ms
    }
  }, []);

  const fetchBillingData = async () => {
    try {
      setIsLoading(true);
      
      // Fetch all billing data in parallel
      const [
        subscriptionRes,
        paymentMethodsRes,
        addressesRes,
        historyRes
      ] = await Promise.all([
        homeownerBillingService.getSubscription(),
        homeownerBillingService.getPaymentMethods(),
        homeownerBillingService.getBillingAddresses(),
        homeownerBillingService.getBillingHistory()
      ]);

      setCurrentPlan(subscriptionRes.data.data);
      setPaymentMethods(paymentMethodsRes.data.data || []);
      setBillingAddresses(addressesRes.data.data || []);
      setBillingHistory(historyRes.data.data || []);
    } catch (error) {
      console.error('Error fetching billing data:', error);
      setToast({
        type: 'error',
        message: 'Failed to load billing information'
      });
    } finally {
      setIsLoading(false);
    }
  };



  const handleAddPaymentMethod = () => {
    setEditingMethod(null);
    setIsModalOpen(true);
  };

  const handleEditPaymentMethod = (method) => {
    setEditingMethod(method);
    setIsModalOpen(true);
  };

  const handleSavePaymentMethod = async (methodData) => {
    try {
      if (editingMethod) {
        // Update existing method
        await homeownerBillingService.updatePaymentMethod(editingMethod.id, methodData);
        setToast({
          type: 'success',
          message: 'Payment method updated successfully'
        });
      } else {
        // Add new method
        await homeownerBillingService.addPaymentMethod(methodData);
        setToast({
          type: 'success',
          message: 'Payment method added successfully'
        });
      }
      
      setIsModalOpen(false);
      setEditingMethod(null);
      fetchBillingData(); // Refresh data
    } catch (error) {
      console.error('Error saving payment method:', error);
      setToast({
        type: 'error',
        message: 'Failed to save payment method'
      });
      return error; // Return error for modal to handle
    }
  };

  const handleDeletePaymentMethod = async (methodId) => {
    if (!window.confirm('Are you sure you want to delete this payment method?')) {
      return;
    }

    try {
      await homeownerBillingService.deletePaymentMethod(methodId);
      setToast({
        type: 'success',
        message: 'Payment method deleted successfully'
      });
      fetchBillingData(); // Refresh data
    } catch (error) {
      console.error('Error deleting payment method:', error);
      console.error('Error response:', error.response?.data);
      
      // Check if it's the specific error about payment method being in use
      if (error.response?.data?.code === 'PAYMENT_METHOD_IN_USE') {
        setToast({
          type: 'error',
          message: 'Cannot delete payment method - it\'s being used by an active subscription. Please cancel your subscription or update it to use a different payment method first.'
        });
      } else if (error.response?.data?.code === 'PAYMENT_METHOD_REFERENCED') {
        setToast({
          type: 'error',
          message: 'Cannot delete payment method - it is referenced by existing subscriptions. Please update or remove the associated subscriptions first.'
        });
      } else if (error.response?.data?.code === 'PAYMENT_METHOD_CONSTRAINT_ERROR') {
        setToast({
          type: 'error',
          message: 'Cannot delete payment method due to database constraint. Please try again or contact support.'
        });
      } else {
        const errorMessage = error.response?.data?.message || 'Failed to delete payment method';
        setToast({
          type: 'error',
          message: errorMessage
        });
      }
    }
  };

  const handleSetDefaultPaymentMethod = async (methodId) => {
    try {
      const method = paymentMethods.find(m => m.id === methodId);
      
      await homeownerBillingService.updatePaymentMethod(methodId, {
        cardHolder: method.card_holder_name,
        expiryMonth: method.expiry_month,
        expiryYear: method.expiry_year,
        isDefault: true
      });
      
      setToast({
        type: 'success',
        message: 'Default payment method updated'
      });
      fetchBillingData(); // Refresh data
    } catch (error) {
      console.error('Error setting default payment method:', error);
      setToast({
        type: 'error',
        message: 'Failed to update default payment method'
      });
    }
  };

  const handleUpdateSubscriptionPaymentMethod = async (newPaymentMethodId) => {
    if (!currentPlan?.id) {
      setToast({
        type: 'error',
        message: 'No active subscription found'
      });
      return;
    }

    try {
      await homeownerBillingService.updateSubscriptionPaymentMethod(currentPlan.id, newPaymentMethodId);
      setToast({
        type: 'success',
        message: 'Subscription payment method updated successfully'
      });
      fetchBillingData(); // Refresh data
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update subscription payment method';
      setToast({
        type: 'error',
        message: errorMessage
      });
      console.error('Error updating subscription payment method:', error);
      throw error; // Re-throw for modal to handle
    }
  };

  const handleAddAddress = () => {
    setEditingAddress(null);
    setIsAddressModalOpen(true);
  };

  const handleEditAddress = (address) => {
    setEditingAddress(address);
    setIsAddressModalOpen(true);
  };

  const handleSaveAddress = async (addressData) => {
    try {
      if (editingAddress) {
        // Update existing address
        await homeownerBillingService.updateBillingAddress(editingAddress.id, addressData);
        setToast({
          type: 'success',
          message: 'Billing address updated successfully'
        });
      } else {
        // Add new address
        await homeownerBillingService.createBillingAddress(addressData);
        setToast({
          type: 'success',
          message: 'Billing address added successfully'
        });
      }
      
      setIsAddressModalOpen(false);
      setEditingAddress(null);
      fetchBillingData(); // Refresh data
    } catch (error) {
      console.error('Error saving billing address:', error);
      setToast({
        type: 'error',
        message: 'Failed to save billing address'
      });
      throw error; // Re-throw for modal to handle
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this billing address?')) {
      return;
    }

    try {
      await homeownerBillingService.deleteBillingAddress(addressId);
      setToast({
        type: 'success',
        message: 'Billing address deleted successfully'
      });
      fetchBillingData(); // Refresh data
    } catch (error) {
      console.error('Error deleting billing address:', error);
      setToast({
        type: 'error',
        message: 'Failed to delete billing address'
      });
    }
  };

  const handleSetDefaultAddress = async (addressId) => {
    try {
      const address = billingAddresses.find(a => a.id === addressId);
      
      await homeownerBillingService.updateBillingAddress(addressId, {
        first_name: address.first_name,
        last_name: address.last_name,
        email: address.email,
        address_line1: address.address_line1,
        address_line2: address.address_line2,
        city: address.city,
        state: address.state,
        postal_code: address.postal_code,
        country: address.country,
        is_default: true
      });
      
      setToast({
        type: 'success',
        message: 'Default billing address updated'
      });
      fetchBillingData(); // Refresh data
    } catch (error) {
      console.error('Error setting default billing address:', error);
      setToast({
        type: 'error',
        message: 'Failed to update default billing address'
      });
    }
  };

  const handleDownloadInvoice = async (invoiceId) => {
    try {
      const response = await homeownerBillingService.downloadInvoice(invoiceId);
      
      // Create a blob from the PDF data
      const blob = new Blob([response.data], { type: 'application/pdf' });
      
      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary link and click it to download
      const link = document.createElement('a');
      link.href = url;
      link.download = `homeowner-invoice-${invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setToast({
        type: 'success',
        message: 'Invoice downloaded successfully'
      });
    } catch (error) {
      setToast({
        type: 'error',
        message: 'Failed to download invoice'
      });
      console.error('Error downloading invoice:', error);
    }
  };

  const handleDownloadAllInvoices = async () => {
    try {
      const response = await homeownerBillingService.downloadAllInvoices();
      
      // Get the content type from response headers to determine file type
      const contentType = response.headers['content-type'] || response.headers['Content-Type'];
      const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
      
      // Extract filename from content-disposition header
      let filename = 'homeowner-invoices';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      // Determine blob type and default filename based on content type
      let blobType, defaultFilename, successMessage;
      if (contentType && contentType.includes('application/pdf')) {
        blobType = 'application/pdf';
        defaultFilename = filename || 'homeowner-invoice.pdf';
        successMessage = 'Invoice downloaded successfully';
      } else if (contentType && contentType.includes('application/zip')) {
        blobType = 'application/zip';
        defaultFilename = filename || 'homeowner-invoices.zip';
        successMessage = 'Invoices downloaded successfully';
      } else {
        // Fallback - try to determine from billingHistory length
        if (billingHistory.length === 1) {
          blobType = 'application/pdf';
          defaultFilename = 'homeowner-invoice.pdf';
          successMessage = 'Invoice downloaded successfully';
        } else {
          blobType = 'application/zip';
          defaultFilename = 'homeowner-invoices.zip';
          successMessage = 'Invoices downloaded successfully';
        }
      }
      
      // Create a blob from the response data
      const blob = new Blob([response.data], { type: blobType });
      
      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary link and click it to download
      const link = document.createElement('a');
      link.href = url;
      link.download = defaultFilename;
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setToast({
        type: 'success',
        message: successMessage
      });
    } catch (error) {
      setToast({
        type: 'error',
        message: 'Failed to download invoices'
      });
      console.error('Error downloading invoices:', error);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await homeownerBillingService.cancelSubscription();
      
      // Update the current plan with cancelled status
      const updatedPlan = {
        ...currentPlan,
        status: 'cancelled',
        nextBilling: response.data?.nextBilling || 
                    response.data?.next_billing_date || 
                    response.data?.data?.nextBilling ||
                    currentPlan.current_period_end
      };

      setCurrentPlan(updatedPlan);
      setToast({
        type: 'success',
        message: 'Subscription cancelled successfully'
      });
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to cancel subscription';
      setToast({
        type: 'error',
        message: errorMessage
      });
      console.error('Error cancelling subscription:', error);
    }
  };

  const handleResumeSubscription = async () => {
    try {
      const response = await homeownerBillingService.resumeSubscription();
      
      if (response.data?.data) {
        // Update the current plan with the resumed subscription data
        const resumedPlan = response.data.data;
        setCurrentPlan({
          ...resumedPlan,
          price: parseFloat(resumedPlan.custom_price || resumedPlan.plan_price) || 0,
          status: 'active'
        });
      } else {
        // Refresh all data if we don't get the updated subscription data
        await fetchBillingData();
      }
      
      setToast({
        type: 'success',
        message: 'Subscription resumed successfully'
      });
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to resume subscription';
      setToast({
        type: 'error',
        message: errorMessage
      });
      console.error('Error resuming subscription:', error);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <i className={`fas fa-spinner fa-spin ${styles.spinner}`}></i>
          <p>Loading billing information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header Section */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1>Billing & Plans</h1>
          <p>Manage your application preferences and configurations</p>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        <div className={styles.billingPlans}>
          {/* Current Plan Section */}
          <section className={styles.currentPlan}>
            <div className={styles.sectionHeader}>
              <h3>Current Plan</h3>
              <div className={styles.planActions}>
                {currentPlan && (
                  <>
                    {currentPlan.status === 'active' && (
                      <>
                        <button 
                          className={styles.updatePaymentButton}
                          onClick={() => setIsUpdatePaymentModalOpen(true)}
                        >
                          <i className="fas fa-credit-card"></i> Update Subscription Payment Method
                        </button>
                      <button 
                        className={styles.cancelButton}
                        onClick={handleCancelSubscription}
                      >
                        <i className="fas fa-times-circle"></i> Cancel Subscription
                      </button>
                      </>
                    )}
                    {currentPlan.status === 'cancelled' && (
                      <button 
                        className={styles.resumeButton}
                        onClick={handleResumeSubscription}
                      >
                        <i className="fas fa-redo"></i> Resume Subscription
                      </button>
                    )}
                    <Link to="/homeowner/pricing" className={styles.changePlanButton}>
                      Change Plan
                    </Link>
                  </>
                )}
              </div>
            </div>
            
            {currentPlan ? (
              <div className={styles.planCard}>
                <div className={styles.planInfo}>
                  <h4>{currentPlan.plan_name || 'No Active Plan'}</h4>
                  <div className={styles.priceInfo}>
                    <span className={styles.price}>
                      ${parseFloat(currentPlan.custom_price || currentPlan.plan_price || 0).toFixed(2)}
                    </span>
                    <span className={styles.billingCycle}>
                      /{(currentPlan.subscription_billing_cycle || currentPlan.plan_billing_cycle) === 'yearly' ? 'year' : 'month'}
                    </span>
                  </div>
                  <p className={styles.billingText}>
                    Billed {(currentPlan.subscription_billing_cycle || currentPlan.plan_billing_cycle) === 'yearly' ? 'annually' : 'monthly'}
                  </p>
                  {currentPlan.property_type && (
                    <p className={styles.propertyInfo}>
                      Property: {currentPlan.property_type} - {currentPlan.property_size}
                    </p>
                  )}
                </div>
                <div className={styles.planStatus}>
                  <div className={styles.statusInfo}>
                    <span className={`${styles.statusBadge} ${styles[currentPlan.status || 'active']}`}>
                      {currentPlan.status === 'cancelled' ? 'Cancelled' : (currentPlan.status || 'Active')}
                    </span>
                    {currentPlan.status === 'cancelled' && (
                      <p className={styles.activeUntil}>
                        Active until {new Date(currentPlan.current_period_end || Date.now()).toLocaleDateString()}
                      </p>
                    )}
                    {currentPlan.status === 'active' && currentPlan.current_period_end && (
                      <p className={styles.nextBilling}>
                        Next billing on {new Date(currentPlan.current_period_end).toLocaleDateString()}
                      </p>
                    )}
                    {currentPlan.status === 'active' && currentPlan.payment_method_brand && currentPlan.payment_method_last_four && (
                      <div className={styles.paymentMethodInfo}>
                        <div className={styles.paymentMethodDisplay}>
                          <PaymentIcon 
                            type={currentPlan.payment_method_brand.toLowerCase()} 
                            format="flat" 
                            width={20} 
                          />
                          <span>{currentPlan.payment_method_brand} •••• {currentPlan.payment_method_last_four}</span>
                        </div>
                      </div>
                    )}
                  </div>
                  {currentPlan.status === 'cancelled' && (
                    <div className={styles.cancellationInfo}>
                      <p>
                        Your subscription has been cancelled but will remain active until {new Date(currentPlan.current_period_end || Date.now()).toLocaleDateString()}. 
                        You can resume your subscription anytime before this date to continue your service.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>
                  <i className="fas fa-credit-card"></i>
                </div>
                <p>No active subscription</p>
                <Link to="/homeowner/pricing" className={styles.primaryButton}>
                  Choose a Plan
                </Link>
              </div>
            )}
          </section>
          
          {/* Payment Methods Section */}
          <section className={styles.paymentMethods}>
            <div className={styles.sectionHeader}>
              <h3>Payment Methods</h3>
              <button 
                className={styles.addButton}
                onClick={handleAddPaymentMethod}
              >
                <i className="fas fa-plus"></i>
                Add Payment Method
              </button>
            </div>
            
            {paymentMethods.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>
                  <i className="fas fa-credit-card"></i>
                </div>
                <p>No payment methods added yet</p>
              </div>
            ) : (
              <div className={styles.methodsList}>
                {paymentMethods.map(method => (
                  <div key={method.id} className={styles.methodCard}>
                    <div className={styles.methodInfo}>
                      <div className={styles.cardDetails}>
                        <div className={styles.cardIconWrapper}>
                          <PaymentIcon 
                            type={method.card_brand.toLowerCase()} 
                            format="flat" 
                            width={48} 
                          />
                        </div>
                        <span>•••• •••• •••• {method.last_four}</span>
                        {Boolean(method.is_default) && (
                          <span className={styles.defaultBadge}>Default</span>
                        )}
                      </div>
                      <p>{method.card_holder_name}</p>
                      <p>Expires {method.expiry_month}/{method.expiry_year}</p>
                    </div>
                    <div className={styles.methodActions}>
                      {!Boolean(method.is_default) && (
                        <button
                          className={styles.setDefaultButton}
                          onClick={() => handleSetDefaultPaymentMethod(method.id)}
                          title="Set as default"
                        >
                          Set Default
                        </button>
                      )}
                      <button
                        className={styles.editButton}
                        onClick={() => handleEditPaymentMethod(method)}
                        title="Edit"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className={styles.deleteButton}
                        onClick={() => handleDeletePaymentMethod(method.id)}
                        title="Delete"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
          
          {/* Billing Addresses Section */}
          <section className={styles.billingAddresses}>
            <div className={styles.sectionHeader}>
              <h3>Billing Addresses</h3>
              <button 
                className={styles.addButton}
                onClick={handleAddAddress}
              >
                <i className="fas fa-plus"></i>
                Add Billing Address
              </button>
            </div>
            
            {billingAddresses.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>
                  <i className="fas fa-map-marker-alt"></i>
                </div>
                <p>No billing addresses added yet</p>
              </div>
            ) : (
              <div className={styles.addressesList}>
                {billingAddresses.map(address => (
                  <div key={address.id} className={styles.addressCard}>
                    <div className={styles.addressCardHeader}>
                      <div className={styles.addressIcon}>
                        <i className="fas fa-home"></i>
                      </div>
                      <div className={styles.addressMainInfo}>
                        <div className={styles.addressName}>
                          <div className={styles.addressNameLeft}>
                            <h4>{address.first_name} {address.last_name}</h4>
                            {Boolean(address.is_default) && (
                              <span className={styles.defaultBadge}>
                                Default
                              </span>
                            )}
                          </div>
                        </div>
                        <div className={styles.addressEmail}>
                          <i className="fas fa-envelope"></i>
                          <span>{address.email}</span>
                        </div>
                      </div>
                      <div className={styles.addressActions}>
                        {!Boolean(address.is_default) && (
                          <button
                            className={styles.setDefaultButton}
                            onClick={() => handleSetDefaultAddress(address.id)}
                            title="Set as default"
                          >
                            Set Default
                          </button>
                        )}
                        <button
                          className={styles.editButton}
                          onClick={() => handleEditAddress(address)}
                          title="Edit"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          className={styles.deleteButton}
                          onClick={() => handleDeleteAddress(address.id)}
                          title="Delete"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                    
                    <div className={styles.addressDetails}>
                      <div className={styles.addressLine}>
                        <i className="fas fa-map-marker-alt"></i>
                        <div className={styles.addressText}>
                          <p>
                            {address.address_line1}
                            {address.address_line2 && `, ${address.address_line2}`}
                          </p>
                          <p>{address.city}, {address.state} {address.postal_code}, {address.country}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className={styles.addressActionsMobile}>
                      {!Boolean(address.is_default) && (
                        <button
                          className={styles.setDefaultButton}
                          onClick={() => handleSetDefaultAddress(address.id)}
                          title="Set as default"
                        >
                          Set Default
                        </button>
                      )}
                      <button
                        className={styles.editButton}
                        onClick={() => handleEditAddress(address)}
                        title="Edit"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className={styles.deleteButton}
                        onClick={() => handleDeleteAddress(address.id)}
                        title="Delete"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Billing History Section */}
          <section className={styles.billingHistory}>
            <div className={styles.sectionHeader}>
              <h3>Billing History</h3>
              {billingHistory.length > 0 && (
                <button 
                  className={styles.addButton}
                  onClick={handleDownloadAllInvoices}
                >
                  <i className="fas fa-download"></i>
                  Download All
                </button>
              )}
            </div>
            
            {billingHistory.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>
                  <i className="fas fa-receipt"></i>
                </div>
                <p>No billing history available</p>
              </div>
            ) : (
              <>
              <div className={styles.historyTable}>
                <table>
                  <thead>
                    <tr>
                      <th>Invoice</th>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {billingHistory.map(invoice => (
                      <tr key={invoice.id}>
                        <td>{invoice.invoice_number}</td>
                        <td>{new Date(invoice.billing_date).toLocaleDateString()}</td>
                        <td>${parseFloat(invoice.amount || 0).toFixed(2)}</td>
                        <td>
                          <span className={`${styles.statusBadge} ${styles[invoice.status]}`}>
                            {invoice.status}
                          </span>
                        </td>
                        <td>
                          <button
                            className={styles.downloadButton}
                            onClick={() => handleDownloadInvoice(invoice.id)}
                            title="Download Invoice"
                          >
                            <i className="fas fa-download"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
                
                <div className={styles.historyCardsMobile}>
                  {billingHistory.map(invoice => (
                    <div key={invoice.id} className={styles.invoiceCard}>
                      <div className={styles.invoiceHeader}>
                        <div className={styles.invoiceInfo}>
                          <h4 className={styles.invoiceNumber}>{invoice.invoice_number}</h4>
                          <p className={styles.invoiceDate}>
                            {new Date(invoice.billing_date).toLocaleDateString()}
                          </p>
                        </div>
                        <span className={`${styles.statusBadge} ${styles[invoice.status]}`}>
                          {invoice.status}
                        </span>
                      </div>
                      <div className={styles.invoiceDetails}>
                        <div className={styles.invoiceAmount}>
                          <span className={styles.amountLabel}>Amount</span>
                          <span className={styles.amountValue}>
                            ${parseFloat(invoice.amount || 0).toFixed(2)}
                          </span>
                        </div>
                        <button
                          className={styles.downloadButtonMobile}
                          onClick={() => handleDownloadInvoice(invoice.id)}
                          title="Download Invoice"
                        >
                          <i className="fas fa-download"></i>
                          Download
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>
        </div>
      </div>

      {/* Modals */}
      <StripeProvider>
        <StripePaymentMethodModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingMethod(null);
        }}
        onSave={handleSavePaymentMethod}
        editingMethod={editingMethod}
      />
      </StripeProvider>

      <BillingAddressModal
        isOpen={isAddressModalOpen}
        onClose={() => {
          setIsAddressModalOpen(false);
          setEditingAddress(null);
        }}
        onSave={handleSaveAddress}
        editingAddress={editingAddress}
      />

      <UpdateSubscriptionPaymentModal
        isOpen={isUpdatePaymentModalOpen}
        onClose={() => setIsUpdatePaymentModalOpen(false)}
        paymentMethods={paymentMethods}
        currentPaymentMethodId={currentPlan?.payment_method_id}
        onUpdate={handleUpdateSubscriptionPaymentMethod}
      />

      {/* Toast Notification */}
      {toast && (
        <Toast 
          type={toast.type} 
          message={toast.message} 
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default HomeOwnerBilling; 